import { Link } from "@tanstack/react-router"
import { ChevronRight } from "lucide-react"
import * as React from "react"
import { SidebarTrigger } from "@/components/ui/sidebar"
import { CommandPaletteTrigger } from "@/features/command-palette/command-palette-trigger"
import { cn } from "@/utils/styles.helpers"
import {
	type NavLocation,
	pageHref,
	resolveTabBar,
	useNavModel,
} from "../sidebar/nav-model"
import { NavTabs } from "../sidebar/nav-tab-bar"
import { ThemeToggle } from "../theme/theme-toggle"

interface Crumb {
	label: string
	href?: string
	mono?: boolean
}

/** Readable label of a path segment below a known page, such as an order number. */
function segmentLabel(segment: string): Crumb {
	const decoded = decodeURIComponent(segment)
	const looksLikeId = /\d/.test(decoded) && decoded.length >= 8

	if (looksLikeId) {
		const short =
			decoded.length > 18
				? `${decoded.slice(0, 8)}…${decoded.slice(-4)}`
				: decoded
		return { label: short, mono: true }
	}

	const words = decoded.replace(/[-_]+/g, " ")
	return { label: words.charAt(0).toUpperCase() + words.slice(1) }
}

/**
 * Where the current route sits: the group, then the page, then what lies
 * below the page's own address (a detail page or a sub-screen).
 */
function buildCrumbs(
	location: NavLocation | undefined,
	pathname: string,
	tabsShown: boolean,
) {
	if (!location) {
		return pathname
			.split("/")
			.filter(Boolean)
			.map((segment) => segmentLabel(segment))
	}

	const { group, page, tab } = location
	const crumbs: Crumb[] = []

	if (group.label !== page.title && group.key !== "overview") {
		crumbs.push({ label: group.label })
	}
	const pageNamedByTab =
		tabsShown && page.tabs.some((pageTab) => pageTab.title === page.title)
	if (!pageNamedByTab) {
		crumbs.push({ label: page.title, href: pageHref(page) })
	}

	const base = [tab.href, ...(tab.activePaths ?? [])].find(
		(path) => pathname === path || pathname.startsWith(`${path}/`),
	)
	const rest = base
		? pathname.slice(base.length).split("/").filter(Boolean)
		: []
	for (const segment of rest) {
		crumbs.push(segmentLabel(segment))
	}

	return crumbs
}

/** Top bar: where you are, the current page's tabs, search and theme. */
export function Header() {
	const { groups, location, pathname } = useNavModel()
	const path =
		pathname.length > 1 && pathname.endsWith("/")
			? pathname.slice(0, -1)
			: pathname
	const tabs = React.useMemo(() => resolveTabBar(groups, path), [groups, path])
	const crumbs = React.useMemo(
		() => buildCrumbs(location, path, tabs !== undefined),
		[location, path, tabs],
	)

	return (
		<>
			<header className="flex h-12 shrink-0 items-center gap-3 border-b px-4">
				<SidebarTrigger className="-ml-1 shrink-0" />
				<div className="flex min-w-0 flex-1 items-center gap-6">
					<nav
						aria-label="Location"
						className="flex min-w-0 items-center gap-1.5 text-sm md:shrink-0"
					>
						{crumbs.map((crumb, index) => {
							const isLast = index === crumbs.length - 1
							const className = cn(
								"truncate",
								!isLast && "hidden md:inline",
								crumb.mono && "font-mono text-[13px]",
								isLast
									? "font-medium text-foreground"
									: "text-muted-foreground",
							)

							return (
								<React.Fragment key={`${crumb.label}-${index}`}>
									{index > 0 && (
										<ChevronRight className="hidden size-3.5 shrink-0 text-muted-foreground/60 md:block" />
									)}
									{crumb.href && !isLast ? (
										<Link
											to={crumb.href}
											className={cn(className, "hover:text-foreground")}
										>
											{crumb.label}
										</Link>
									) : (
										<span
											className={className}
											aria-current={isLast ? "page" : undefined}
										>
											{crumb.label}
										</span>
									)}
								</React.Fragment>
							)
						})}
					</nav>
					{tabs && (
						<div className="hidden min-w-0 md:block">
							<NavTabs match={tabs} />
						</div>
					)}
				</div>
				<CommandPaletteTrigger />
				<ThemeToggle />
			</header>
			{tabs && (
				<div className="shrink-0 border-b px-4 md:hidden">
					<NavTabs match={tabs} />
				</div>
			)}
		</>
	)
}
